import { createContext, Dispatch, FC, PropsWithChildren, SetStateAction, useContext, useMemo, useState } from 'react';
import { EMPTY_SNAPSHOT, Snapshot, useGetSnapshot } from '../api/queries/useGetSnapshot';
import useHasPermissions from '../../../hooks/useHasPermissions';
import { Pages, Roles } from '../../../context/contexts/UserContext/permissionsTypes';
import { usePolicyEnabled } from '../../../components/Policy/hooks/usePolicyEnabled';
import { DaysRange } from '../types/DaysRange';

interface ActiveDirectoryAccessContextProperties {
    hasAccessIntelligenceEditorRole: boolean;
    hasPolicyEnabled: boolean;
    refetchSnapshot: () => Promise<void>;
    selectedStaleResourcesDaysRange: DaysRange;
    setSelectedStaleResourcesDaysRange: Dispatch<SetStateAction<DaysRange>>;
    snapshot: Snapshot;
    snapshotLoading: boolean;
}

const defaultProperties: ActiveDirectoryAccessContextProperties = {
    hasAccessIntelligenceEditorRole: false,
    hasPolicyEnabled: false,
    refetchSnapshot: async () => {},
    selectedStaleResourcesDaysRange: DaysRange['30_DAYS'],
    setSelectedStaleResourcesDaysRange: () => {},
    snapshot: EMPTY_SNAPSHOT,
    snapshotLoading: false
};

export const ActiveDirectoryAccessContext = createContext<ActiveDirectoryAccessContextProperties>(defaultProperties);

export const ActiveDirectoryAccessProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
    const [selectedStaleResourcesDaysRange, setSelectedStaleResourcesDaysRange] = useState<DaysRange>(
        DaysRange['30_DAYS']
    );

    const { refetchSnapshot, snapshot, snapshotLoading } = useGetSnapshot();

    const hasAccessIntelligenceEditorRole = useHasPermissions({
        permission: Pages.ACCESS_ANALYSIS,
        role: Roles.EDITOR
    });
    const hasPolicyEnabled = usePolicyEnabled() ?? false;

    const contextValue = useMemo(
        () => ({
            hasAccessIntelligenceEditorRole,
            hasPolicyEnabled,
            refetchSnapshot,
            selectedStaleResourcesDaysRange,
            setSelectedStaleResourcesDaysRange,
            snapshot,
            snapshotLoading
        }),
        [
            hasAccessIntelligenceEditorRole,
            hasPolicyEnabled,
            refetchSnapshot,
            selectedStaleResourcesDaysRange,
            setSelectedStaleResourcesDaysRange,
            snapshot,
            snapshotLoading
        ]
    );

    return (
        <ActiveDirectoryAccessContext.Provider value={contextValue}>{children}</ActiveDirectoryAccessContext.Provider>
    );
};

export const useActiveDirectoryAccessContext = (): ActiveDirectoryAccessContextProperties =>
    useContext(ActiveDirectoryAccessContext);
